/**
 * Calendar Cell Renderer
 * 
 * Unified rendering for calendar day cells used across calendar views.
 */

import { isToday, DAY_NAMES } from './date-utils.js';
import { renderEventBars, type EventBarConfig } from './event-bar-renderer.js';
import { designTokens } from './design-system.js';

export interface CalendarCellConfig {
  date: Date;
  dayNumber: number;
  isCurrentMonth?: boolean;
  events?: Array<{
    id: string;
    title: string;
    priority?: 'low' | 'medium' | 'high';
    time?: string;
  }>;
  maxVisibleEvents?: number;
  onClick?: () => void;
  onEventClick?: (eventId: string) => void;
}

/**
 * Render a calendar day cell
 */
export function renderCalendarCell(config: CalendarCellConfig): string {
  const {
    date,
    dayNumber,
    isCurrentMonth = true,
    events = [],
    maxVisibleEvents = 3,
    onClick,
    onEventClick
  } = config;
  
  const today = isToday(date);
  const bgColor = today 
    ? 'rgba(255, 255, 255, 0.15)' 
    : !isCurrentMonth 
    ? 'rgba(0, 0, 0, 0.05)' 
    : 'transparent';
  
  const textColor = isCurrentMonth 
    ? designTokens.contentColor 
    : designTokens.contentColorMuted;
  
  const visibleEvents = events.slice(0, maxVisibleEvents);
  const remainingCount = events.length - maxVisibleEvents;
  
  // Set up click handlers
  if (onEventClick) {
    (window as any).__eventBarClick = (eventId: string) => {
      onEventClick(eventId);
    };
  }
  
  const eventBars: EventBarConfig[] = visibleEvents.map(event => ({
    id: event.id,
    title: event.title,
    priority: event.priority,
    onClick: onEventClick ? () => onEventClick(event.id) : undefined
  }));
  
  const clickHandler = onClick 
    ? `onclick="window.__calendarCellClick && window.__calendarCellClick(${date.getTime()});"`
    : '';
  
  if (onClick) {
    (window as any).__calendarCellClick = (timestamp: number) => {
      onClick();
    };
  }
  
  return `
    <div 
      class="calendar-day" 
      data-date="${date.getTime()}"
      style="
        padding: ${designTokens.spacing.cellPadding};
        background: ${bgColor};
        display: flex;
        flex-direction: column;
        min-height: 0;
        cursor: ${onClick ? 'pointer' : 'default'};
        border-radius: ${designTokens.radius.sm};
        transition: background 0.2s;
      "
      ${clickHandler}
    >
      <div style="
        font-size: ${designTokens.fontSize.sm};
        font-weight: ${today ? 'bold' : 'normal'};
        color: ${textColor};
        margin-bottom: ${designTokens.spacing.cellPadding};
      ">
        ${dayNumber}
      </div>
      <div style="flex: 1; display: flex; flex-direction: column; gap: 2px; min-height: 0; overflow: hidden;">
        ${renderEventBars(eventBars)}
      </div>
      ${remainingCount > 0 ? `
        <div style="
          font-size: ${designTokens.fontSize.xs};
          color: ${designTokens.contentColorMuted};
          margin-top: auto;
        ">
          +${remainingCount}
        </div>
      ` : ''}
    </div>
  `;
}

/**
 * Render calendar day names header
 */
export function renderCalendarDayNames(): string {
  return `
    <div style="
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: ${designTokens.spacing.gridGap};
      margin-bottom: ${designTokens.spacing.cellPadding};
    ">
      ${DAY_NAMES.map(name => `
        <div style="
          font-size: ${designTokens.fontSize.xs};
          font-weight: 600;
          color: ${designTokens.contentColorMuted};
          text-align: center;
          padding: ${designTokens.padding.xs};
        ">${name}</div>
      `).join('')}
    </div>
  `;
}

